/**
 * The AuthProvider component is used to provide the authentication context to the application.
 * It is used to check the authentication status of the user and to provide the user with the appropriate routes.
 */

import { createContext, useContext, useEffect, useState, useMemo } from 'react';

import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import Welcome from 'views/Welcome';
import PasswordRecovery from 'views/Welcome/components/PasswordRecovery';
import { SnackbarProvider } from 'notistack';
import Home from 'views/Home';
import Dashboard from 'views/Dashboard';
import ProtectedRoute from './ProtectedRoute';
import Browse from 'views/OrganizationLogs/Browse';
import PersonalSettings from 'views/PersonalSettings';
import { getStaticMenuContents } from 'views/root/routes';
import { DOLPHIN_API } from 'shared/constants/apis';
import useApi from 'shared/hooks/api';
import { setSavedName, getSavedName } from 'utils/name';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import api from 'utils/api';
import PageError from 'components/PageError';
import logger from 'utils/logger';
import ProtectedAdminRoute from './ProtectedAdminRoute';

import Organizations from 'views/admin/Organizations';
import FillData from 'views/admin/FillData';
import Users from 'views/admin/Users';
import Contracts from 'views/admin/Contracts';
import Employments from 'views/admin/Employments';
import Tag from 'views/admin/Tag';
import AdminLLMPrompts from 'views/admin/LLMPrompts';
import ParsedTextForIssues from 'views/admin/ParsedTextForIssues';
import LLMPrivileges from 'views/admin/LLMPrivileges';
import AdminDashboard from 'views/admin/components/Dashboard';
import {
  UserType,
  SpecialUserMessages,
  SIGNIN_SESSION,
} from 'shared/constants/users';
import { IWG_GROUP } from './constants';
import AdminReportTemplates from 'views/admin/ReportTemplates';

const USER_STATUS = {
  NOT_SIGNED_IN: 'NotSignedIn',
  NOT_CHECKED: 'NotChecked',
};

const HOME_PATH = {
  admin: '/admin/dashboard/users',
  user: '/dashboard/home',
};

const LOGIN_STATUS = {
  NOT_CHECKED: 'NOT_CHECKED',
  NO_EMPLOYMENTS: 'NO_EMPLOYMENTS',
  COMPLETED: 'COMPLETED',
};

const ERROR_CODES = [404, 403];

// Authentication Context
const AuthContext = createContext({
  isAuthenticated: false,
  currentUser: null,
  userGroups: null,
  currentUserInfo: null,
  isLoading: true,
  login: () => {},
  logout: () => {},
  authError: null,
});

// Create a logger instance for this component
const log = logger.createLogger('AuthProvider');

const AuthProvider = () => {
  // State declarations
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [currentUser, setCurrentUser] = useState(USER_STATUS.NOT_CHECKED);
  const [userGroups, setUserGroups] = useState(null);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldFetchUser, setShouldFetchUser] = useState(false);
  const [currentLoginStatus, setCurrentLoginStatus] = useState(LOGIN_STATUS);

  // Define initial routes
  const INITIAL_ROUTES = useMemo(() => {
    const redirectPath = userGroups?.includes(IWG_GROUP)
      ? HOME_PATH.admin
      : HOME_PATH.user;
    return [
      {
        path: '/login',
        element: (
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Welcome />
          </SnackbarProvider>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <PasswordRecovery />
          </SnackbarProvider>
        ),
      },
      {
        path: '/',
        element: <ProtectedRoute />,
        errorElement: <PageError />,
        children: [
          {
            index: true,
            element: <Navigate to={redirectPath} replace />,
          },
          {
            path: '/dashboard',
            element: <Dashboard />,
            children: [
              {
                index: true,
                element: <Navigate to={redirectPath} replace />,
              },
              { path: '/dashboard/home', element: <Home /> },
              {
                path: '/dashboard/browse/issues/:issueId',
                element: <Browse />,
              },
              {
                path: '/dashboard/personal-settings',
                element: <PersonalSettings />,
              },
            ],
          },
          { path: '*', element: <Navigate to="/dashboard/home" replace /> },
        ],
      },
      {
        path: '/admin',
        element: <ProtectedAdminRoute />,
        errorElement: <PageError />,
        children: [
          {
            index: true,
            element: <Navigate to="/admin/dashboard/users" replace />,
          },
          {
            path: '/admin/dashboard',
            element: <AdminDashboard />,
            children: [
              { path: '/admin/dashboard/users', element: <Users /> },
              { path: '/admin/dashboard/orgs', element: <Organizations /> },
              {
                path: '/admin/dashboard/employments',
                element: <Employments />,
              },
              { path: '/admin/dashboard/contracts', element: <Contracts /> },
              { path: '/admin/dashboard/fill-data', element: <FillData /> },
              { path: '/admin/dashboard/tags', element: <Tag /> },
              {
                path: '/admin/dashboard/llm-prompts',
                element: <AdminLLMPrompts />,
              },
              {
                path: '/admin/dashboard/reports-templates',
                element: <AdminReportTemplates />,
              },
              {
                path: '/admin/dashboard/llm-privileges',
                element: <LLMPrivileges />,
              },
              {
                path: '/admin/dashboard/parsed-issues',
                element: <ParsedTextForIssues />,
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to="/admin/dashboard/users" replace />,
          },
        ],
      },
    ];
  }, [userGroups]);

  // Fetch user data API
  const skipFetch = !shouldFetchUser;
  const [{ data: userData, error: userError }] = useApi.useGet(
    DOLPHIN_API.USERS,
    {
      skip: skipFetch,
    }
  );

  // Build routes based on user privileges
  const obtainRoutes = () => {
    if (
      !localData.get(USER_DATA.CURRENT_ORGANIZATION) ||
      !localData.get(USER_DATA.CURRENT_PRIVILEGE)
    ) {
      return INITIAL_ROUTES;
    }

    const staticRoutes = getStaticMenuContents();
    const updatedRoutes = [...INITIAL_ROUTES];

    // Find the dashboard route
    const dashboardRoute = updatedRoutes[2]?.children?.find(
      (route) => route.path === '/dashboard'
    );

    if (dashboardRoute) {
      // Keep only static routes
      dashboardRoute.children = dashboardRoute.children.filter(
        (route) =>
          route.index === true ||
          [
            '/dashboard/home',
            '/dashboard/browse/issues/:issueId',
            '/dashboard/personal-settings',
          ].includes(route.path)
      );

      // Process and add dynamic routes
      const newRoutes = staticRoutes
        .map((route) => {
          if (route.type === 'collapse') {
            return route.children.map((child) => {
              const routeObj = {
                path: child.route,
                element: child.component,
              };
              if (child.children) {
                routeObj.children = child.children.map((child) => ({
                  path: child.route,
                  element: child.component,
                }));
              }
              return routeObj;
            });
          } else if (route.type === 'item') {
            const routeObj = {
              path: route.route,
              element: route.component,
            };
            if (route.children) {
              routeObj.children = route.children.map((child) => ({
                path: child.route,
                element: child.component,
              }));
            }
            return routeObj;
          } else {
            return {
              path: route.route,
              element: route.component,
            };
          }
        })
        .flat();

      dashboardRoute.children = [...dashboardRoute.children, ...newRoutes];
    }

    return updatedRoutes;
  };

  // Initialize routes
  const [routes, setRoutes] = useState(() => obtainRoutes());

  // Setup user data in local storage
  const setupUserData = (userInfo) => {
    log.info('setupUserData', userInfo);

    if (!userInfo || userInfo.message === SpecialUserMessages.ADMIN) return;

    // Check employments if none the show modal to inform the user to create an employment
    if (userInfo.employments.length === 0) {
      setCurrentLoginStatus(LOGIN_STATUS.NO_EMPLOYMENTS);
      return;
    }

    // Extract organization names
    const organizationNames =
      userInfo.organizations?.map((org) => org.name) || [];

    // Check if user data needs to be updated
    const needsUserUpdate =
      !localData.get(USER_DATA.USERNAME) ||
      localData.get(USER_DATA.USERNAME) === 'undefined' ||
      localData.get(USER_DATA.USERNAME) !== userInfo.username ||
      !userInfo.organizations ||
      (userInfo.organizations &&
        !organizationNames.includes(
          localData.get(USER_DATA.CURRENT_ORGANIZATION)
        ));

    // Update basic user data if needed
    if (needsUserUpdate) {
      localData.set(USER_DATA.USERNAME, userInfo.username);
      localData.set(USER_DATA.USER_ID, userInfo.id);
      setSavedName(userInfo.firstName, userInfo.lastName);
      localData.set(USER_DATA.AVATAR_URL, userInfo.avatarUrl);
      localData.set(
        USER_DATA.CURRENT_ORGANIZATION,
        userInfo.organizations ? userInfo.organizations[0].name : null
      );
      localData.set(USER_DATA.PREFFERED_LANGUAGE, userInfo.preferredLanguage);
      localData.set(USER_DATA.TIMEZONE, userInfo.preferredTimezone);
      localData.set(USER_DATA.EMAIL, userInfo.email);
    }

    // Handle organization ID
    let currentOrganizationId = Number(
      localData.get(USER_DATA.CURRENT_ORGANIZATION_ID)
    );

    if (!currentOrganizationId || needsUserUpdate) {
      currentOrganizationId = userInfo.organizations
        ? userInfo.organizations[0].id
        : -1;
    } else if (userInfo.organizations) {
      const organization = userInfo.organizations.find(
        (org) => org.id === currentOrganizationId
      );
      if (!organization) {
        currentOrganizationId = -1;
      }
    }

    localData.set(
      USER_DATA.CURRENT_ORGANIZATION_ID,
      Number(currentOrganizationId)
    );

    // send signin notification in case of a new sign-in
    if (
      !localData.get('SIGNIN_SESSION_STATUS') ||
      localData.get('SIGNIN_SESSION_STATUS') !== SIGNIN_SESSION.COMPLETED
    ) {
      api.post(`${DOLPHIN_API.USERS}/signin-organization`, {
        data: {
          username: userInfo.username,
          userId: userInfo.id,
          organizationId: currentOrganizationId,
        },
      });
      localData.set('SIGNIN_SESSION_STATUS', SIGNIN_SESSION.COMPLETED);
    }

    // Set ownership status
    const currentOrganization = userInfo.organizations.find(
      (org) => org.id === currentOrganizationId
    );
    localData.set(
      USER_DATA.IS_OWNER,
      currentOrganization?.ownerId === userInfo.id
    );

    // Set privileges
    const currentEmployment = userInfo.employments.find(
      (emp) => emp.organizationId === currentOrganizationId
    );
    localData.set(
      USER_DATA.CURRENT_PRIVILEGE,
      currentEmployment ? currentEmployment.privilege : {}
    );

    //! Fallback to staff if userType is not set
    localData.set(
      USER_DATA.USER_TYPE,
      currentEmployment?.userType || UserType.STAFF
    );

    // Update name if changed
    const { savedFirstName, savedLastName } = getSavedName();
    if (
      savedFirstName !== userInfo.firstName ||
      savedLastName !== userInfo.lastName
    ) {
      setSavedName(userInfo.firstName, userInfo.lastName);
    }

    // Update preferred language if changed
    if (
      localData.get(USER_DATA.PREFFERED_LANGUAGE) !== userInfo.preferredLanguage
    ) {
      localData.set(USER_DATA.PREFFERED_LANGUAGE, userInfo.preferredLanguage);
    }
    setCurrentLoginStatus(LOGIN_STATUS.COMPLETED);
  };

  const login = async () => {
    setAuthError(null);
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      setCurrentUser(user);
      setUserGroups(session.tokens.accessToken.payload['cognito:groups']);
      setIsAuthenticated(true);
      setIsLoading(false);
      setShouldFetchUser(true);
    } catch (error) {
      log.error('Error signing in:', error);
      setCurrentUser(USER_STATUS.NOT_SIGNED_IN);
      setIsAuthenticated(false);
      setIsLoading(false);
      setShouldFetchUser(false);
      localData.remove('SIGNIN_SESSION_STATUS');
    }
  };

  // Authentication check on mount
  useEffect(() => {
    login();
  }, []);

  // Handle user data changes
  useEffect(() => {
    if (userData) {
      setCurrentUserInfo(userData);
      setupUserData(userData);
      setRoutes(obtainRoutes());
    }
    if (userError) {
      log.error('Error fetching user data', { error: userError });
      if (ERROR_CODES.includes(userError.response.status)) {
        setAuthError(userError.response.status);
        if (userError.response.status === 404) {
          logout();
        }
      }
    }
  }, [userData, userError]);

  // Logout function
  const logout = async () => {
    try {
      await signOut();
      setCurrentLoginStatus(LOGIN_STATUS.NOT_CHECKED);
      setCurrentUser(USER_STATUS.NOT_SIGNED_IN);
      setIsAuthenticated(false);
      setUserGroups(null);
      setCurrentUserInfo(null);
      setShouldFetchUser(false);
    } catch (error) {
      log.error('Error signing out:', error);
    }
  };

  // Context value
  const authContextValue = useMemo(
    () => ({
      isAuthenticated,
      currentUser,
      userGroups,
      currentUserInfo,
      isLoading,
      login,
      logout,
      authError,
    }),
    [
      isAuthenticated,
      currentUser,
      userGroups,
      currentUserInfo,
      isLoading,
      authError,
    ]
  );

  if (currentLoginStatus === LOGIN_STATUS.NO_EMPLOYMENTS) {
    return <PageError errorCode={998} logoutFunction={logout} />;
  }
  return (
    <AuthContext.Provider value={authContextValue}>
      <RouterProvider
        router={createBrowserRouter(routes, {
          future: {
            v7_relativeSplatPath: true,
          },
        })}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
